import React, { useState, useEffect, useCallback } from 'react';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import { api } from '@libero/core/services/api';
import { AlertCircle, Info, Loader2, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface AutoCandidate {
  variationId: number;
  itemId: number;
  variantennr: string;
  artikelname: string;
  isMain: boolean;
  kanalVorhanden: boolean;
  kanalAktion: 'setzen' | 'vorhanden';
  kategorieVorhanden: boolean;
  kategorieAktion: 'schreiben' | 'bereits-gesetzt';
  bestehendeKategorieId: string | null;
  vorschlagSelectionId: number | null;
  vorschlagSelectionName: string;
  quelle: 'name' | 'kategorie' | 'set' | 'keiner' | null;
  /** Tiefste Shop-Kategorie der Variante. '—' wenn keine bekannt. */
  shopKategorie: string;
  /** Aktuelle bekannte Verkaufskanaele (aktuell nur Kaufland), kommagetrennt. '—' wenn keiner. */
  verkaufskanaele: string;
  /** Aktuell in Property 107 gesetzter Kaufland-Kategorie-Name. '—' wenn (noch) leer. */
  aktuelleKauflandKategorie: string;
}

interface HerstellerOption {
  producerId: number;
  producerName: string;
}

/** Hersteller-Uebersicht (POST /auto/uebersicht) — zeigt wo noch Arbeit liegt. */
interface HerstellerUebersichtZeile {
  producerId: number;
  producerName: string;
  total: number;
  fertig: number;
  offen: number;
}

/**
 * Job-resultSummary des Scans (Streaming-Umbau 16.07.): NUR Zaehler + Hersteller —
 * die Zeilen selbst liegen in der Kandidaten-Tabelle und werden serverseitig
 * paginiert (POST /auto/candidates-page mit scanJobId + bucket).
 */
interface LoadSummary {
  scanJobId: string;
  kandidatenCount: number;
  manuellPruefenCount: number;
  setsCount: number;
  gescannt: number;
  bereitsVollstaendig: number;
  hersteller: HerstellerOption[];
  clientsSynced: boolean;
}

interface CandidatesPage {
  rows: AutoCandidate[];
  page: number;
  pageSize: number;
  total: number;
}

interface AutoKpi {
  gefiltert: number;
  mitKategorie: number;
  ohneKategorie: number;
  mitKanal: number;
  ohneKanal: number;
  clientsSynced: boolean;
}

interface ApplyFieldResult {
  geschrieben: number;
  bereitsGesetzt: number;
  fehler: number;
  fehlerDetails: string[];
}

interface ApplyResult {
  total: number;
  kanal: ApplyFieldResult;
  kategorie: ApplyFieldResult & { duplicateBatches: number };
}

interface RemoveResult {
  total: number;
  entfernt: number;
  bereitsEntfernt: number;
  fehler: number;
  fehlerDetails: string[];
}

const PAGE_SIZE = 200;
/** Auswahlen bis zu dieser Groesse gelten als Testlauf (keine Massenlauf-Bestaetigung noetig). */
const TESTRUN_THRESHOLD = 20;

/** 1:1 aus backend/src/config.ts PROPERTY_WRITE_BATCH_SIZE — nur fuer die Dauer-Schaetzung. */
const WRITE_BATCH_SIZE = 500;

/** 1:1 aus backend/src/config.ts MANDANTEN — bei Aenderung dort AUCH hier pflegen (kein Endpoint dafuer, stabile Business-Konstante). */
const MANDANTEN = [
  { plentyId: 11897, label: 'anstoss24.de' },
  { plentyId: 46807, label: 'Fussballtore.de' },
] as const;

const QUELLE_BADGE: Record<Exclude<AutoCandidate['quelle'], null>, { label: string; className: string }> = {
  name: { label: 'Name', className: 'bg-blue-100 text-blue-800' },
  kategorie: { label: 'Kategorie', className: 'bg-purple-100 text-purple-800' },
  set: { label: 'Set', className: 'bg-gray-200 text-gray-700' },
  keiner: { label: 'kein Treffer', className: 'bg-amber-100 text-amber-800' },
};

const KANAL_BADGE: Record<AutoCandidate['kanalAktion'], { label: string; className: string }> = {
  setzen: { label: 'wird gesetzt', className: 'bg-emerald-100 text-emerald-800' },
  vorhanden: { label: 'vorhanden', className: 'bg-gray-100 text-gray-600' },
};

const KATEGORIE_BADGE: Record<AutoCandidate['kategorieAktion'], { label: string; className: string }> = {
  schreiben: { label: 'wird geschrieben', className: 'bg-emerald-100 text-emerald-800' },
  'bereits-gesetzt': { label: 'bereits gesetzt', className: 'bg-gray-100 text-gray-600' },
};

export default function AutoTab() {
  const {
    status: loadStatus, isRunning: loadRunning, progress: loadProgress,
    result: loadResultRaw, error: loadError, startJob: startLoadJob, reset: resetLoadJob,
  } = useJobStatus(null);

  const {
    status: applyStatus, isRunning: applyRunning, progress: applyProgress,
    result: applyResultRaw, error: applyError, startJob: startApplyJob, reset: resetApplyJob,
  } = useJobStatus(null);

  const {
    status: removeStatus, isRunning: removeRunning, progress: removeProgress,
    result: removeResultRaw, error: removeError, startJob: startRemoveJob, reset: resetRemoveJob,
  } = useJobStatus(null);

  const summary = loadResultRaw as LoadSummary | null;
  const applyResult = applyResultRaw as ApplyResult | null;
  const removeResult = removeResultRaw as RemoveResult | null;

  // Auswahl-Modell (Streaming-Umbau): die Kandidaten liegen serverseitig — die Auswahl ist
  // deshalb ENTWEDER "alle minus abgewaehlte" (Default nach dem Scan) ODER eine explizite
  // ID-Menge (nach "Auswahl leeren" / "Testlauf mit N"). Aufgeloest wird sie beim Apply im Backend.
  const [selMode, setSelMode] = useState<'all' | 'ids'>('all');
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  const [included, setIncluded] = useState<Set<number>>(new Set());

  const [page, setPage] = useState(1);
  const [pageRows, setPageRows] = useState<AutoCandidate[]>([]);
  const [pageLoading, setPageLoading] = useState(false);

  const [showManuell, setShowManuell] = useState(false);
  const [showSets, setShowSets] = useState(false);
  const [manuellRows, setManuellRows] = useState<AutoCandidate[] | null>(null);
  const [setsRows, setSetsRows] = useState<AutoCandidate[] | null>(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [testN, setTestN] = useState(5);
  const [massenlaufBestaetigt, setMassenlaufBestaetigt] = useState(false);

  const [aktivStatus, setAktivStatus] = useState<'aktiv' | 'inaktiv' | 'beide'>('aktiv');
  // Default: nur anstoss24.de vorausgewaehlt (entspricht dem bisherigen Phase-1-Gate-Verhalten)
  const [selectedMandanten, setSelectedMandanten] = useState<Set<number>>(new Set([11897]));
  const [onlySets, setOnlySets] = useState(false);
  const [selectedHersteller, setSelectedHersteller] = useState<Set<number>>(new Set());
  const [herstellerOptions, setHerstellerOptions] = useState<HerstellerOption[]>([]);
  const [kpi, setKpi] = useState<AutoKpi | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);

  const [showUebersicht, setShowUebersicht] = useState(false);
  const [uebersichtRows, setUebersichtRows] = useState<HerstellerUebersichtZeile[]>([]);
  const [uebersichtLoading, setUebersichtLoading] = useState(false);

  const kandidatenCount = summary?.kandidatenCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(kandidatenCount / PAGE_SIZE));
  const selectedCount = selMode === 'all' ? Math.max(0, kandidatenCount - excluded.size) : included.size;

  const fetchCandidatesPage = useCallback(async (
    scanJobId: string, bucket: 'kandidat' | 'manuell' | 'set', p: number, pageSize: number,
  ): Promise<CandidatesPage> => {
    const r = await api.post<{ success: boolean; data: CandidatesPage }>(
      '/programmes/kaufland-kategorisierung/auto/candidates-page',
      { scanJobId, bucket, page: p, pageSize },
    );
    return r.data.data;
  }, []);

  async function handleLoad() {
    setSelMode('all');
    setExcluded(new Set());
    setIncluded(new Set());
    setPage(1);
    setPageRows([]);
    setManuellRows(null);
    setSetsRows(null);
    setShowManuell(false);
    setShowSets(false);
    setMassenlaufBestaetigt(false);
    resetApplyJob();
    resetRemoveJob();
    await startLoadJob('/programmes/kaufland-kategorisierung/auto/load', {
      aktivStatus,
      clientIds: Array.from(selectedMandanten),
      producerIds: Array.from(selectedHersteller),
      onlySets,
    });
  }

  // Scan fertig: Hersteller-Optionen der Filtermenge uebernehmen (praeziser als die Basis-Liste),
  // Auswahl = alle Kandidaten (Nutzer kann gezielt abwaehlen). Zeilen holt der Seiten-Effect.
  useEffect(() => {
    if (loadStatus === 'completed' && summary) {
      setSelMode('all');
      setExcluded(new Set());
      setIncluded(new Set());
      setMassenlaufBestaetigt(false);
      setPage(1);
      if (summary.hersteller.length > 0) setHerstellerOptions(summary.hersteller);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStatus]);

  // Kandidaten-Seite serverseitig laden (bei neuem Scan-Ergebnis und bei Seitenwechsel).
  useEffect(() => {
    if (!summary?.scanJobId || summary.kandidatenCount === 0) { setPageRows([]); return; }
    let cancelled = false;
    setPageLoading(true);
    fetchCandidatesPage(summary.scanJobId, 'kandidat', page, PAGE_SIZE)
      .then(r => { if (!cancelled) setPageRows(r.rows); })
      .catch(() => { if (!cancelled) setPageRows([]); })
      .finally(() => { if (!cancelled) setPageLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.scanJobId, page]);

  // Hersteller-Dropdown: fuellt sich SOFORT aus der Registry (eigener Endpoint, kein Scan noetig).
  useEffect(() => {
    let cancelled = false;
    async function fetchHersteller() {
      try {
        const r = await api.post<{ success: boolean; data: { hersteller: HerstellerOption[] } }>(
          '/programmes/kaufland-kategorisierung/auto/hersteller',
          { aktivStatus, clientIds: Array.from(selectedMandanten) },
        );
        if (!cancelled) setHerstellerOptions(r.data.data.hersteller);
      } catch {
        /* Dropdown bleibt dann leer — kein harter Fehler */
      }
    }
    fetchHersteller();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktivStatus, JSON.stringify(Array.from(selectedMandanten).sort())]);

  // Hersteller-Uebersicht: laedt EINMAL beim Seitenaufruf + bei Aenderung von Aktiv-Status/Mandant
  // (analog zum Hersteller-Dropdown). Fehler still schlucken — Block bleibt dann leer/ausgeblendet.
  useEffect(() => {
    let cancelled = false;
    async function fetchUebersicht() {
      setUebersichtLoading(true);
      try {
        const r = await api.post<{ success: boolean; data: { hersteller: HerstellerUebersichtZeile[] } }>(
          '/programmes/kaufland-kategorisierung/auto/uebersicht',
          { aktivStatus, clientIds: Array.from(selectedMandanten) },
        );
        if (!cancelled) setUebersichtRows(r.data.data.hersteller);
      } catch {
        if (!cancelled) setUebersichtRows([]);
      } finally {
        if (!cancelled) setUebersichtLoading(false);
      }
    }
    fetchUebersicht();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktivStatus, JSON.stringify(Array.from(selectedMandanten).sort())]);

  // KPI-Div: aktualisiert sich automatisch bei Filteraenderung (kein "Kandidaten laden" noetig).
  useEffect(() => {
    let cancelled = false;
    async function fetchKpi() {
      setKpiLoading(true);
      try {
        const r = await api.post<{ success: boolean; data: AutoKpi }>(
          '/programmes/kaufland-kategorisierung/auto/kpi',
          { aktivStatus, clientIds: Array.from(selectedMandanten), producerIds: Array.from(selectedHersteller) },
        );
        if (!cancelled) setKpi(r.data.data);
      } catch {
        if (!cancelled) setKpi(null);
      } finally {
        if (!cancelled) setKpiLoading(false);
      }
    }
    fetchKpi();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktivStatus, JSON.stringify(Array.from(selectedMandanten).sort()), JSON.stringify(Array.from(selectedHersteller).sort())]);

  // Manuell-/Sets-Listen lazy laden (erste 500) beim ersten Aufklappen.
  useEffect(() => {
    if (!showManuell || manuellRows !== null || !summary?.scanJobId) return;
    fetchCandidatesPage(summary.scanJobId, 'manuell', 1, 500).then(r => setManuellRows(r.rows)).catch(() => setManuellRows([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showManuell]);
  useEffect(() => {
    if (!showSets || setsRows !== null || !summary?.scanJobId) return;
    fetchCandidatesPage(summary.scanJobId, 'set', 1, 500).then(r => setSetsRows(r.rows)).catch(() => setSetsRows([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSets]);

  const [removeIdsRaw, setRemoveIdsRaw] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removeMassenlaufBestaetigt, setRemoveMassenlaufBestaetigt] = useState(false);

  const removeIds = React.useMemo(
    () => Array.from(new Set(
      removeIdsRaw.split(/[\s,;]+/).map(s => Number(s.trim())).filter(Number.isFinite),
    )),
    [removeIdsRaw],
  );
  const isRemoveMassenlauf = removeIds.length > TESTRUN_THRESHOLD;

  async function handleRemove() {
    setShowRemoveConfirm(false);
    setRemoveMassenlaufBestaetigt(false);
    if (removeIds.length === 0) return;
    await startRemoveJob('/programmes/kaufland-kategorisierung/auto/remove', { variationIds: removeIds });
  }

  function isChecked(id: number): boolean {
    return selMode === 'all' ? !excluded.has(id) : included.has(id);
  }

  function toggleOne(id: number) {
    setMassenlaufBestaetigt(false);
    if (selMode === 'all') {
      setExcluded(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    } else {
      setIncluded(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    }
  }

  function selectAll() {
    setMassenlaufBestaetigt(false);
    setSelMode('all');
    setExcluded(new Set());
  }

  function selectNone() {
    setMassenlaufBestaetigt(false);
    setSelMode('ids');
    setIncluded(new Set());
  }

  async function selectTestrun() {
    if (!summary?.scanJobId) return;
    setMassenlaufBestaetigt(false);
    const n = Math.max(1, testN);
    try {
      const r = await fetchCandidatesPage(summary.scanJobId, 'kandidat', 1, n);
      setSelMode('ids');
      setIncluded(new Set(r.rows.map(c => c.variationId)));
    } catch {
      /* Auswahl bleibt unveraendert */
    }
  }

  async function handleApply() {
    setShowConfirm(false);
    setMassenlaufBestaetigt(false);
    if (!summary?.scanJobId || selectedCount === 0) return;
    const selection = selMode === 'all'
      ? { mode: 'all' as const, excludedIds: Array.from(excluded) }
      : { mode: 'ids' as const, ids: Array.from(included) };
    await startApplyJob('/programmes/kaufland-kategorisierung/auto/apply', {
      scanJobId: summary.scanJobId,
      selection,
    });
  }

  // Grobe Dauer-Schaetzung: 1 Batch = bis zu WRITE_BATCH_SIZE Eintraege = 1 Write. Plenty-Limit ~75 Writes/h.
  const estBatches = Math.ceil(selectedCount / WRITE_BATCH_SIZE);
  const estHours = estBatches > 75 ? Math.ceil(estBatches / 75) : 0;
  const isMassenlauf = selectedCount > TESTRUN_THRESHOLD;

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="bg-[#E9FF57]/20 border border-[#E9FF57] rounded-lg p-4 mb-6 flex gap-3 items-start">
        <Info className="text-[#1A1A1A] mt-0.5 flex-shrink-0" size={20} />
        <div className="text-sm text-[#1A1A1A]">
          <p className="font-semibold mb-1">Ablauf (Auto-Zuordnung Property 107)</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>„Kandidaten laden" scannt Teambekleidung (aktiv, Mandant anstoss24.de aktiv, keine Sets) und ermittelt je Variante zwei unabhaengige Aktionen: Kanal Kaufland DE setzen (falls noch nicht vorhanden) und Kategorie-Vorschlag schreiben (falls Property 107 noch leer — Artikelname zuerst, aktive Plenty-Kategorie als Fallback).</li>
            <li>Vorschau zeigt beide Aktionen getrennt. Varianten ohne Kategorie-Treffer landen in „manuell pruefen" (bekommen GAR KEINEN automatischen Write), Sets werden separat ausgewiesen (komplett ausgeschlossen) — beide werden NIE automatisch geschrieben.</li>
            <li>Testlauf zuerst: einzelne Checkboxen ODER „Testlauf mit N" nutzen, Ergebnis in Plenty pruefen. Erst danach eine groessere Auswahl schreiben — dafuer verlangt der Bestaetigungsdialog ab {TESTRUN_THRESHOLD}+ Varianten eine explizite Massenlauf-Bestaetigung. Write-only-if-empty: bereits gesetzte Werte werden nie ueberschrieben.</li>
          </ol>
        </div>
      </div>

      <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-[#1A1A1A] mb-3">Filter</h3>
        <div className="flex flex-wrap gap-8">
          <div>
            <div className="text-xs text-gray-600 mb-1">Aktiv-Status</div>
            <div className="flex gap-3 text-sm">
              {(['aktiv', 'inaktiv', 'beide'] as const).map(v => (
                <label key={v} className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="aktivStatus" checked={aktivStatus === v} onChange={() => setAktivStatus(v)} />
                  {v === 'aktiv' ? 'Nur aktiv' : v === 'inaktiv' ? 'Nur inaktiv' : 'Beide'}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Mandant</div>
            <div className="flex gap-3 text-sm">
              {MANDANTEN.map(m => (
                <label key={m.plentyId} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedMandanten.has(m.plentyId)}
                    onChange={() => setSelectedMandanten(prev => {
                      const next = new Set(prev);
                      if (next.has(m.plentyId)) next.delete(m.plentyId); else next.add(m.plentyId);
                      return next;
                    })}
                  />
                  {m.label}
                </label>
              ))}
            </div>
            {kpi && !kpi.clientsSynced && (
              <p className="text-xs text-amber-700 mt-1 max-w-xs">
                Mandant-Daten warten auf den ersten Sync-Lauf nach der Registry-Migration — der Mandant-Filter
                wirkt erst danach zuverlaessig.
              </p>
            )}
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Sets</div>
            <label className="flex items-center gap-1 text-sm cursor-pointer">
              <input type="checkbox" checked={onlySets} onChange={e => setOnlySets(e.target.checked)} />
              Nur Sets zeigen (informativ, nie automatisch geschrieben)
            </label>
          </div>
          <div className="min-w-[220px]">
            <div className="text-xs text-gray-600 mb-1">Hersteller</div>
            <select
              multiple
              value={Array.from(selectedHersteller).map(String)}
              onChange={e => setSelectedHersteller(new Set(Array.from(e.target.selectedOptions).map(o => Number(o.value))))}
              className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm h-20"
            >
              {herstellerOptions.map(h => (
                <option key={h.producerId} value={h.producerId}>{h.producerName}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Leer = alle Hersteller.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#DAD9D9] rounded-lg overflow-hidden mb-6">
        <button
          onClick={() => setShowUebersicht(v => !v)}
          className="w-full px-4 py-2 bg-gray-50 border-b border-[#DAD9D9] flex items-center justify-between text-sm font-medium text-[#1A1A1A]"
        >
          Hersteller-Uebersicht (wo liegt Arbeit?)
          {showUebersicht ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showUebersicht && (
          <div className="max-h-96 overflow-y-auto">
            {uebersichtLoading && (
              <p className="text-sm text-gray-500 p-3"><Loader2 className="animate-spin inline mr-2" size={14} /> Laedt...</p>
            )}
            {!uebersichtLoading && uebersichtRows.length === 0 && (
              <p className="text-sm text-gray-500 p-3">Keine Daten.</p>
            )}
            {!uebersichtLoading && uebersichtRows.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left border-b border-[#DAD9D9]">
                    <th className="px-3 py-2">Hersteller</th>
                    <th className="px-3 py-2 text-right">Varianten</th>
                    <th className="px-3 py-2 text-right">fertig</th>
                    <th className="px-3 py-2 text-right">offen</th>
                  </tr>
                </thead>
                <tbody>
                  {uebersichtRows.map(r => (
                    <tr key={r.producerId} className="border-b border-gray-100">
                      <td className={`px-3 py-1.5 ${r.offen === 0 ? 'text-gray-500' : 'text-[#1A1A1A]'}`}>{r.producerName}</td>
                      <td className="px-3 py-1.5 text-right">{r.total.toLocaleString('de-DE')}</td>
                      <td className="px-3 py-1.5 text-right">{r.fertig.toLocaleString('de-DE')}</td>
                      <td className="px-3 py-1.5 text-right">
                        {r.offen === 0
                          ? <span className="text-green-700">✓ fertig</span>
                          : <strong className="text-amber-700">{r.offen.toLocaleString('de-DE')}</strong>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-[#1A1A1A] mb-2">
          KPI (aktuelle Aktiv-/Mandant-/Hersteller-Filtermenge — Sets-Filter wirkt hier NICHT, da Name-basiert)
        </h3>
        {kpiLoading && <p className="text-sm text-gray-500">Laedt...</p>}
        {!kpiLoading && kpi && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Gefiltert (Gesamt)" value={kpi.gefiltert} color="#3B82F6" />
            <SummaryCard label="Mit Kategorie" value={kpi.mitKategorie} color="#10B981" />
            <SummaryCard label="Ohne Kategorie" value={kpi.ohneKategorie} color="#F59E0B" />
            <SummaryCard label="Mit Kanal (Kaufland DE)" value={kpi.mitKanal} color="#10B981" />
            <SummaryCard label="Ohne Kanal" value={kpi.ohneKanal} color="#F59E0B" />
          </div>
        )}
      </div>

      <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
        <button
          onClick={handleLoad}
          disabled={loadRunning}
          className="px-4 py-2 bg-[#E9FF57] text-[#1A1A1A] font-medium rounded hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loadRunning ? <Loader2 className="animate-spin" size={16} /> : null}
          {loadRunning ? 'Lade Kandidaten...' : 'Kandidaten laden'}
        </button>

        {loadRunning && (
          <div className="mt-3">
            <div className="h-2 bg-[#DAD9D9] rounded overflow-hidden">
              <div
                className="h-2 bg-[#E9FF57] rounded transition-all duration-300"
                style={{ width: loadProgress ? `${loadProgress.percentage}%` : '10%' }}
              />
            </div>
            {loadProgress?.message && <p className="text-sm mt-1 text-gray-600">{loadProgress.message}</p>}
          </div>
        )}

        {loadError && !loadRunning && (
          <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle size={14} /> {loadError}
          </div>
        )}
      </div>

      {summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            <SummaryCard label="Gescannt (nach Filtern)" value={summary.gescannt} color="#3B82F6" />
            <SummaryCard label="Schreibbar (Kandidaten)" value={summary.kandidatenCount} color="#10B981" />
            <SummaryCard label="Manuell pruefen" value={summary.manuellPruefenCount} color="#F59E0B" />
            <SummaryCard label="Sets" value={summary.setsCount} color="#8B5CF6" />
            <SummaryCard label="Bereits vollstaendig" value={summary.bereitsVollstaendig} color="#6B7280" />
          </div>

          {/* Nichts zu tun: ohne diesen Hinweis bliebe die Flaeche komplett leer (keine der drei
              Listen wird gerendert) — das sah aus wie ein Fehlschlag, obwohl der Scan korrekt
              "alles schon zugeordnet" gemeldet hat (Matze 29.07.2026, Fall GLA-WEL 194/194).
              gescannt===0 wird bewusst getrennt behandelt: "Alle 0 Varianten sind fertig" waere
              irrefuehrend, wenn der Filter einfach nichts trifft (z.B. "Nur inaktiv"). */}
          {summary.kandidatenCount === 0 && summary.manuellPruefenCount === 0 && summary.setsCount === 0 && (
            summary.gescannt === 0 ? (
              <div className="bg-gray-50 border border-[#DAD9D9] rounded-lg p-4 mb-4 flex items-start gap-3">
                <Info className="text-gray-500 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-sm text-gray-700">
                  <strong>Keine Varianten in dieser Filtermenge.</strong> Aktiv-Status, Mandant oder
                  Hersteller-Auswahl pruefen.
                </p>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                <CheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-sm text-green-800">
                  <strong>Alle {summary.bereitsVollstaendig.toLocaleString('de-DE')} Varianten</strong> der aktuellen
                  Filtermenge sind bereits vollstaendig zugeordnet (Kanal Kaufland DE + Kategorie) —
                  nichts zu tun.
                </p>
              </div>
            )
          )}

          {summary.kandidatenCount > 0 && (
            <div className="bg-white border border-[#DAD9D9] rounded-lg overflow-hidden mb-4">
              <div className="px-4 py-2 bg-gray-50 border-b border-[#DAD9D9] flex flex-wrap items-center gap-2 text-sm">
                <button onClick={selectAll} className="px-3 py-1.5 bg-white border border-[#DAD9D9] rounded hover:bg-gray-50">Alle auswaehlen</button>
                <button onClick={selectNone} className="px-3 py-1.5 bg-white border border-[#DAD9D9] rounded hover:bg-gray-50">Auswahl leeren</button>
                <div className="flex items-center gap-1.5 ml-2">
                  <label className="text-gray-600">Testlauf mit</label>
                  <input
                    type="number"
                    min={1}
                    max={Math.min(500, Math.max(1, summary.kandidatenCount))}
                    value={testN}
                    onChange={e => setTestN(Math.max(1, Number(e.target.value) || 1))}
                    className="w-16 px-2 py-1 border border-[#DAD9D9] rounded"
                  />
                  <button onClick={selectTestrun} className="px-3 py-1.5 bg-white border border-[#DAD9D9] rounded hover:bg-gray-50">
                    Nur diese N auswaehlen
                  </button>
                </div>
                <span className="text-gray-600 font-medium">{selectedCount.toLocaleString('de-DE')} / {summary.kandidatenCount.toLocaleString('de-DE')} ausgewaehlt</span>
              </div>

              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left border-b border-[#DAD9D9]">
                      <th className="px-2 py-2 w-10"></th>
                      <th className="px-3 py-2">VariantenNr</th>
                      <th className="px-3 py-2">Artikelname</th>
                      <th className="px-3 py-2">Shop-Kategorie</th>
                      <th className="px-3 py-2">Verkaufskanäle</th>
                      <th className="px-3 py-2 w-24">Kanal</th>
                      <th className="px-3 py-2">Aktuell (Kaufland)</th>
                      <th className="px-3 py-2">Vorschlag</th>
                      <th className="px-3 py-2 w-28">Quelle</th>
                      <th className="px-3 py-2 w-32">Kategorie-Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageLoading && (
                      <tr>
                        <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                          <Loader2 className="animate-spin inline mr-2" size={16} /> Lade Seite {page}...
                        </td>
                      </tr>
                    )}
                    {!pageLoading && pageRows.map(c => (
                      <tr key={c.variationId} className={`border-b border-gray-100 ${isChecked(c.variationId) ? 'bg-[#E9FF57]/10' : ''}`}>
                        <td className="px-2 py-2">
                          <input type="checkbox" checked={isChecked(c.variationId)} onChange={() => toggleOne(c.variationId)} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{c.variantennr || `#${c.variationId}`}</td>
                        <td className="px-3 py-2">{c.artikelname}</td>
                        <td className="px-3 py-2 text-gray-600">{c.shopKategorie}</td>
                        <td className="px-3 py-2 text-gray-600">{c.verkaufskanaele}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${KANAL_BADGE[c.kanalAktion].className}`}>{KANAL_BADGE[c.kanalAktion].label}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{c.aktuelleKauflandKategorie}</td>
                        <td className="px-3 py-2">{c.vorschlagSelectionName}</td>
                        <td className="px-3 py-2">
                          {c.quelle && <span className={`text-xs px-2 py-0.5 rounded ${QUELLE_BADGE[c.quelle].className}`}>{QUELLE_BADGE[c.quelle].label}</span>}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${KATEGORIE_BADGE[c.kategorieAktion].className}`}>{KATEGORIE_BADGE[c.kategorieAktion].label}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-2 bg-gray-50 border-t border-[#DAD9D9] flex items-center gap-3 text-sm">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || pageLoading} className="px-2 py-1 bg-white border border-[#DAD9D9] rounded disabled:opacity-40">Zurueck</button>
                <span className="text-gray-600">Seite {page} / {totalPages} · {summary.kandidatenCount.toLocaleString('de-DE')} Kandidaten</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || pageLoading} className="px-2 py-1 bg-white border border-[#DAD9D9] rounded disabled:opacity-40">Weiter</button>
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t border-[#DAD9D9] flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-gray-700">
                  {selectedCount.toLocaleString('de-DE')} ausgewaehlt
                  {estHours > 0 && <span className="text-amber-700"> · Plenty-Write-Limit ~75 Batches/h → ca. {estHours} h.</span>}
                </div>
                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={applyRunning || selectedCount === 0}
                  className={`px-4 py-2 font-medium rounded flex items-center gap-2 ${applyRunning || selectedCount === 0 ? 'bg-[#DAD9D9] text-[#1A1A1A] cursor-not-allowed opacity-60' : 'bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]'}`}
                >
                  {applyRunning ? <><Loader2 className="animate-spin" size={16} /> Schreibt...</> : `${selectedCount.toLocaleString('de-DE')} in Plenty schreiben`}
                </button>
              </div>

              {applyRunning && (
                <div className="px-4 pb-3">
                  <div className="h-2 bg-[#DAD9D9] rounded overflow-hidden">
                    <div className="h-2 bg-[#E9FF57] rounded transition-all duration-300" style={{ width: applyProgress ? `${applyProgress.percentage}%` : '10%' }} />
                  </div>
                  {applyProgress?.message && <p className="text-sm mt-1 text-gray-600">{applyProgress.message}</p>}
                </div>
              )}
            </div>
          )}

          {summary.manuellPruefenCount > 0 && (
            <CollapsibleList
              title={`Manuell pruefen (${summary.manuellPruefenCount.toLocaleString('de-DE')})`}
              open={showManuell}
              onToggle={() => setShowManuell(v => !v)}
              rows={manuellRows}
              total={summary.manuellPruefenCount}
            />
          )}

          {summary.setsCount > 0 && (
            <CollapsibleList
              title={`Sets — bewusst ausgeschlossen (${summary.setsCount.toLocaleString('de-DE')})`}
              open={showSets}
              onToggle={() => setShowSets(v => !v)}
              rows={setsRows}
              total={summary.setsCount}
            />
          )}
        </>
      )}

      <div className="bg-white border border-red-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-[#1A1A1A] mb-2">Kaufland-Kategorie entfernen (Property 107)</h3>
        <p className="text-sm text-gray-600 mb-2">
          Loescht NUR die Kaufland.de-Kategorie-Eigenschaft (Property 107) der unten eingetragenen
          Varianten-IDs. Der Kaufland-Kanal (102.01) bleibt unangetastet. Variations-IDs eingeben (eine pro
          Zeile oder mit Komma getrennt) — Testmodus: erst mit 1-2 IDs pruefen, dann groessere Auswahl.
        </p>
        <textarea
          value={removeIdsRaw}
          onChange={e => setRemoveIdsRaw(e.target.value)}
          placeholder={'z.B.\n58873\n684209'}
          className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm font-mono h-24"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm text-gray-600">{removeIds.length.toLocaleString('de-DE')} Varianten-ID(s) erkannt</span>
          <button
            onClick={() => setShowRemoveConfirm(true)}
            disabled={removeRunning || removeIds.length === 0}
            className={`px-4 py-2 font-medium rounded flex items-center gap-2 ${removeRunning || removeIds.length === 0 ? 'bg-[#DAD9D9] text-[#1A1A1A] cursor-not-allowed opacity-60' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
          >
            {removeRunning ? <><Loader2 className="animate-spin" size={16} /> Entfernt...</> : 'Kategorie entfernen'}
          </button>
        </div>

        {removeRunning && (
          <div className="mt-3">
            <div className="h-2 bg-[#DAD9D9] rounded overflow-hidden">
              <div className="h-2 bg-red-400 rounded transition-all duration-300" style={{ width: removeProgress ? `${removeProgress.percentage}%` : '10%' }} />
            </div>
            {removeProgress?.message && <p className="text-sm mt-1 text-gray-600">{removeProgress.message}</p>}
          </div>
        )}

        {(removeStatus === 'completed' || removeStatus === 'failed') && (
          <div className={`mt-3 rounded-lg border p-3 text-sm ${removeStatus === 'completed' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            {removeStatus === 'completed' && removeResult && (
              <div className="space-y-1">
                <div>Entfernt: <strong className="text-green-700">{removeResult.entfernt.toLocaleString('de-DE')}</strong></div>
                <div>Bereits entfernt/nicht gefunden: <strong>{removeResult.bereitsEntfernt.toLocaleString('de-DE')}</strong></div>
                {removeResult.fehler > 0 && <div className="text-red-700">Fehler: <strong>{removeResult.fehler}</strong></div>}
                {removeResult.fehlerDetails.length > 0 && (
                  <details className="text-xs mt-1">
                    <summary className="cursor-pointer text-red-600">Fehlerdetails ({removeResult.fehlerDetails.length})</summary>
                    <ul className="mt-1 list-disc list-inside text-red-700 space-y-0.5">
                      {removeResult.fehlerDetails.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
            {removeStatus === 'failed' && removeError && <p className="text-red-700">{removeError}</p>}
          </div>
        )}
      </div>

      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">Entfernen bestaetigen</h2>
            <p className="text-gray-700 mb-3">
              <strong>{removeIds.length.toLocaleString('de-DE')}</strong> {removeIds.length === 1 ? 'Variante' : 'Varianten'}:
              Property 107 (Kaufland.de-Kategorie) wird geloescht. Der Kanal (102.01) bleibt bestehen.
            </p>
            <p className="text-sm text-amber-700 mb-4">Achtung: Schreibt (loescht) direkt in Produktions-Plenty.</p>
            {isRemoveMassenlauf && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3">
                <label className="flex items-start gap-2 text-sm text-amber-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={removeMassenlaufBestaetigt}
                    onChange={e => setRemoveMassenlaufBestaetigt(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Ich habe einen Testlauf mit wenigen Varianten erfolgreich geprueft und bestaetige diesen
                    Massen-Lauf mit {removeIds.length.toLocaleString('de-DE')} Varianten.
                  </span>
                </label>
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowRemoveConfirm(false)} className="px-4 py-2 bg-white border border-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-50">Abbrechen</button>
              <button
                onClick={handleRemove}
                disabled={isRemoveMassenlauf && !removeMassenlaufBestaetigt}
                className={`px-4 py-2 font-medium rounded ${isRemoveMassenlauf && !removeMassenlaufBestaetigt ? 'bg-[#DAD9D9] text-[#1A1A1A] cursor-not-allowed opacity-60' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
              >
                Jetzt entfernen
              </button>
            </div>
          </div>
        </div>
      )}

      {(applyStatus === 'completed' || applyStatus === 'failed') && (
        <div className={`mb-4 rounded-lg border p-4 ${applyStatus === 'completed' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-start gap-3">
            {applyStatus === 'completed' ? <CheckCircle className="text-green-600 mt-0.5" size={20} /> : <AlertCircle className="text-red-600 mt-0.5" size={20} />}
            <div className="flex-1">
              <h3 className="font-semibold text-[#1A1A1A] mb-2">{applyStatus === 'completed' ? 'Schreibvorgang abgeschlossen' : 'Schreibvorgang fehlgeschlagen'}</h3>
              {applyStatus === 'completed' && applyResult && (
                <div className="text-sm text-gray-700 space-y-3">
                  <div>
                    <div className="font-semibold text-[#1A1A1A] mb-1">Kanal (Kaufland DE, 102.01)</div>
                    <div>Geschrieben: <strong className="text-green-700">{applyResult.kanal.geschrieben.toLocaleString('de-DE')}</strong></div>
                    <div>Bereits gesetzt (uebersprungen): <strong>{applyResult.kanal.bereitsGesetzt.toLocaleString('de-DE')}</strong></div>
                    {applyResult.kanal.fehler > 0 && <div className="text-red-700">Fehler: <strong>{applyResult.kanal.fehler}</strong></div>}
                  </div>
                  <div>
                    <div className="font-semibold text-[#1A1A1A] mb-1">Kategorie (Property 107)</div>
                    <div>Geschrieben: <strong className="text-green-700">{applyResult.kategorie.geschrieben.toLocaleString('de-DE')}</strong></div>
                    <div>Bereits gesetzt (uebersprungen): <strong>{applyResult.kategorie.bereitsGesetzt.toLocaleString('de-DE')}</strong></div>
                    {applyResult.kategorie.duplicateBatches > 0 && <div>Duplicate-Batches (informativ, kein Fehler): <strong>{applyResult.kategorie.duplicateBatches}</strong></div>}
                    {applyResult.kategorie.fehler > 0 && <div className="text-red-700">Fehler: <strong>{applyResult.kategorie.fehler}</strong></div>}
                  </div>
                  {(applyResult.kanal.fehlerDetails.length > 0 || applyResult.kategorie.fehlerDetails.length > 0) && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-red-600">
                        Fehlerdetails ({applyResult.kanal.fehlerDetails.length + applyResult.kategorie.fehlerDetails.length})
                      </summary>
                      <ul className="mt-1 list-disc list-inside text-red-700 space-y-0.5">
                        {applyResult.kanal.fehlerDetails.map((e, i) => <li key={`k-${i}`}>Kanal: {e}</li>)}
                        {applyResult.kategorie.fehlerDetails.map((e, i) => <li key={`c-${i}`}>Kategorie: {e}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              {applyStatus === 'failed' && applyError && <p className="text-sm text-red-700">{applyError}</p>}
            </div>
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">Schreiben bestaetigen</h2>
            <p className="text-gray-700 mb-3">
              <strong>{selectedCount.toLocaleString('de-DE')}</strong> {selectedCount === 1 ? 'Variante wird' : 'Varianten werden'} in Plenty geschrieben (Kanal- und/oder Kategorie-Feld(er), write-only-if-empty).
            </p>
            {estHours > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3 text-xs text-amber-800">
                <strong>WRITE-Limit:</strong> Plenty erlaubt max. 75 Batch-Writes/Stunde ({WRITE_BATCH_SIZE} Eintraege/Batch).
                Bei {estBatches.toLocaleString('de-DE')} Batches laeuft der Job ca. {estHours} h im Hintergrund.
              </div>
            )}
            <p className="text-sm text-amber-700 mb-4">Achtung: Schreibt direkt in Produktions-Plenty. Testmodus = nur die ausgewaehlten Varianten werden beschrieben.</p>
            {isMassenlauf && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3">
                <label className="flex items-start gap-2 text-sm text-amber-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={massenlaufBestaetigt}
                    onChange={e => setMassenlaufBestaetigt(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Ich habe einen Testlauf mit wenigen Varianten erfolgreich in Plenty geprueft und bestaetige
                    diesen Massen-Lauf mit {selectedCount.toLocaleString('de-DE')} Varianten.
                  </span>
                </label>
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowConfirm(false)} className="px-4 py-2 bg-white border border-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-50">Abbrechen</button>
              <button
                onClick={handleApply}
                disabled={isMassenlauf && !massenlaufBestaetigt}
                className={`px-4 py-2 font-medium rounded ${isMassenlauf && !massenlaufBestaetigt ? 'bg-[#DAD9D9] text-[#1A1A1A] cursor-not-allowed opacity-60' : 'bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]'}`}
              >
                Jetzt schreiben
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-[#DAD9D9] rounded p-3">
      <div className="text-xs text-gray-600">{label}</div>
      <div className="text-xl font-bold" style={{ color }}>{value.toLocaleString('de-DE')}</div>
    </div>
  );
}

function CollapsibleList({ title, open, onToggle, rows, total }: {
  title: string;
  open: boolean;
  onToggle: () => void;
  /** null = noch nicht geladen (lazy beim ersten Aufklappen, erste 500 Zeilen). */
  rows: Array<Pick<AutoCandidate, 'variationId' | 'variantennr' | 'artikelname'>> | null;
  total: number;
}) {
  return (
    <div className="bg-white border border-[#DAD9D9] rounded-lg overflow-hidden mb-4">
      <button onClick={onToggle} className="w-full px-4 py-2 bg-gray-50 border-b border-[#DAD9D9] flex items-center justify-between text-sm font-medium text-[#1A1A1A]">
        {title}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="max-h-64 overflow-y-auto">
          {rows === null && (
            <p className="text-sm text-gray-500 p-3"><Loader2 className="animate-spin inline mr-2" size={14} /> Laedt...</p>
          )}
          {rows !== null && (
            <>
              <table className="w-full text-sm">
                <tbody>
                  {rows.map(c => (
                    <tr key={c.variationId} className="border-b border-gray-100">
                      <td className="px-3 py-1.5 font-mono text-xs">{c.variantennr || `#${c.variationId}`}</td>
                      <td className="px-3 py-1.5">{c.artikelname}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {total > rows.length && <p className="text-xs text-gray-500 p-2">... und {(total - rows.length).toLocaleString('de-DE')} weitere.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
